// Components
const ModalTrackingFilter = () => import(/* webpackPrefetch: true, webpackChunkName: "modal-modify-treament" */ '@/app/views/main-view/tracking/components/track-device-component/modals/track-device-modal/track-device-filter-modal.vue');

// Mock Data
import TrackingComponentMockData from '@/app/views/main-view/tracking/components/track-device-component/track-device-component-unit-test/track-device-component-mock-data.js'

// Services
import trackingHistoryService from '@/app/services/telstra/tracking-history/tracking-history-service.js';

// Mixins
import { tableMixins } from '@/app/mixins/utils/table-mixins.js';
import { dateMixins } from '@/app/mixins/utils/date-mixins.js';
import { toastMessageMixins } from '@/app/mixins/utils/toast-message-mixins.js';

// Constants
import { STATUS_CODES, ERROR_MSG, TOKEN } from '@/app/constants/constants.js';

// Event Bus
import EventBus from '@/app/bus/event-bus.js';

// Third party libraries
import { numeric } from 'vuelidate/lib/validators';
import { format, subDays, differenceInCalendarDays } from 'date-fns';
import DatePicker from 'vue2-datepicker';
import { gmapApi } from "vue2-google-maps";

export default {
	name: 'TrackingComponent',
	components: {
		DatePicker,
		ModalTrackingFilter
	},
	mixins: [
		tableMixins,
		dateMixins,
		toastMessageMixins
	],
	data() {
		return {
			// laoders
			isAPILoading: true,
			// data
			trackingComponentMockData: new TrackingComponentMockData,
			// maps
			infoWindow: {
				show: false,
				index: numeric
			},
			trackDisabled: false,
			mapCenter : { lat: -33.865143, lng: 151.209900 },
			mapZoom: 18,
			// markers
			markers: [],
			markerOptions: {
				url: require('@/assets/images/tracking/marker-orange.png'),
				size: { width: 35, height: 45, f: 'px', b: 'px' },
				scaledSize: { width: 35, height: 45, f: 'px', b: 'px' },
			},
			// filter
			intervalOptions: [],
			filter: {
				dateRange: [
					subDays(new Date, 7),
					new Date()
				],
				interval: {
					value: '10 minutes',
					option: 10
				}
			},
			// table
			theads: [
				{ name: 'Address', sortLabel: null, isDesc: false, style: null},
				{ name: 'Date and Time', sortLabel: 'signalDateTime', isDesc: false, style: null},
				{ name: 'Battery Level', sortLabel: 'batteryLevel', isDesc: false, style: null},
				{ name: 'Accuracy', sortLabel: 'accuracy', isDesc: false, style: null},
			],
			// sorting
			sortBy: 'signalDateTime',
			desc: false,
			// modal
			isModalShown: false
		}
	},
	computed: {
		selectedInterval() {
			return this.filter.interval;
		},
		serialNumber() {
			return this.$route.params.id;
		},
		google: gmapApi
	},
	created() {
		this.getTableContent();
		this.setIntervalOptions('week');
	},
	methods: {
		backToTracking() {
			this.$router.push('/tracking');
		},
		closeModal() {
			this.isModalShown = false;
		},
		onSelectedInterval(interval) {
			this.filter.interval = interval;
		},
		setInfoWindow(index) {
			if (index === this.infoWindow.index) {
				this.infoWindow.show = !this.infoWindow.show;
			} else {
				this.infoWindow.show = true;
				this.infoWindow.index = index;
			}

			this.setZoomLevel();
		},
		setZoomLevel() {
			let bounds = new this.google.maps.LatLngBounds();

			for (let marker of this.markers) {
				bounds.extend(marker.position);
			}

			this.$refs.map.fitBounds(bounds);
		},
		batteryLevelClass(batteryLevel) {
			if (batteryLevel >= 50) {
				return 'badge-success';
			} else if (batteryLevel < 50 && batteryLevel >= 20) {
				return 'badge-warning';
			} else if (batteryLevel < 20) {
				return 'badge-danger';
			}
		},
		setDateRange(date) {
			this.filter.dateRange = date;
			this.setDuration();
		},
		setDuration() {
			if (this.filter.dateRange[1] === null) {
				this.trackDisabled = true;
			} else {
				const days = differenceInCalendarDays(this.filter.dateRange[1], this.filter.dateRange[0]);
				let duration = '';

				if (days < 7) {
					duration = 'week';
				} else if (days < 30) {
					duration = 'month';
				} else {
					duration = 'monthAbove';
				}

				this.setIntervalOptions(duration);
			}
		},
		updateFilter(filter) {
			this.filter = filter ? filter : this.filter;
			this.page = 1;
			this.getTableContent();
		},
		showTrackingModal() {
			this.isModalShown = true;
			this.$refs.modalTracking.show();
		},
		getTableContent(formFilter, page = this.page, limit = this.limit, sortBy = this.sortBy, desc = this.desc) {
			this.getTrackingHistory({ page, limit, sortBy, desc });
		},
		getTrackingHistory({page, limit, sortBy, desc}) {
			this.isAPILoading = true;
			const serialNumber = this.serialNumber;

			const trackingDetails = {
				startDate: format(new Date(this.filter.dateRange[0]), 'yyyy-MM-dd'),
				endDate: format(new Date(this.filter.dateRange[1]), 'yyyy-MM-dd'),
				interval: this.filter.interval.option,
				serialNumber,
				page, limit, sortBy, desc
			};

			trackingHistoryService.getTrackingDetails(trackingDetails)
				.then(async res => {
					if (res.status === STATUS_CODES.SUCCESS) {
                        this.tableContent = {
							tableData: res.data.data,
							paginationData: res.data.pagination
						};

						this.setMapMarkers(this.tableContent.tableData);
						this.setMapCenter(this.tableContent.tableData);
						this.isAPILoading = false;
						EventBus.$emit('setMapHeight');

					} else {
						// Set user's current location on the map when it errors.
						this.geoLocate();
						let errorMessage = null;
						let errorTitle = null;
						if (res.data && res.data.fault && res.data.fault.faultstring) {
							errorMessage = res.data.fault.faultstring;
						} else if (res.data && res.data.errors && res.data.errors[0].message){
							errorTitle = res.data.errors[0].message;
							errorMessage = this.toastMessages.tracking().ERROR_INVALID_SERIAL_NUMBER;
						}

						if (res.status === STATUS_CODES.UNAUTHORIZED
							&& errorMessage === TOKEN.TELSTRA_EXPIRED) {
							// Request for new telstra token if expired
							this.getTableContent();

						} else {
							this.showCustomErrorToast(errorTitle || ERROR_MSG.INTERNAL_ERROR_TITLE, errorMessage);
							this.isAPILoading = false;
							EventBus.$emit('setMapHeight');
						}
					}
				})
				.catch(err => {
					this.showGenericErrorToast();
					this.isAPILoading = false;
				});
		},
		geoLocate() {
			// Only works if user allows permission to get current location.
			navigator.geolocation.getCurrentPosition(position => {
				this.mapCenter = {
					lat: position.coords.latitude,
					lng: position.coords.longitude
				}
			})
		},
		setMapMarkers(tableData) {
			this.infoWindow.show = false;
			this.markers = [];

			tableData.forEach(element => {
				this.markers.push({
					position: { lat: element.latitude, lng: element.longitude },
					title: element.id,
					accuracy: element.accuracy,
					options: {
						strokeColor: '#478cf5',
						strokeOpacity: 0.6,
						strokeWeight: 2,
						fillColor: '#478cf5',
						fillOpacity: 0.3
					}
				});
			});

		},
		setMapCenter(tableData) {
			this.mapCenter = { lat: -33.865143, lng: 151.209900 };

			if (tableData.length > 0) {
				this.mapCenter = { lat: tableData[0].latitude, lng: tableData[0].longitude };
			}
		},
		setIntervalOptions(duration) {
			this.intervalOptions = this.trackingComponentMockData.getIntervalOptions()[duration];
			this.filter.interval = this.intervalOptions[0];
			this.trackDisabled = false;
		}
	}
}